import { useRef, useEffect } from 'react';
import { Portal } from 'react-portal';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../common/themes';
import { setIsImageViewerOpen } from '../features/layoutSlice';
import IconX from './IconX';

const useStyles = makeStyles(
	() => ({
		root: {
			width: '100%',
			height: '100%',
			position: 'absolute',
			top: '0px',
			backgroundColor: 'rgba(0, 0, 0, 0.5)',
			left: '0px',
			zIndex: 9999,
		},
		titleSection: {
			marginTop: 16,
			color: color.$7,
		},
		img: {
			width: '70vw',
			maxHeight: '70vh',
			objectFit: 'contain',
		},
		closeStyle: {
			position: 'absolute',
			top: 12,
			right: 12,
			cursor: 'pointer',
		},
		leftStyle: {
			position: 'absolute',
			top: '50%',
			left: 32,
			cursor: 'pointer',
		},
		rightStyle: {
			position: 'absolute',
			top: '50%',
			right: 32,
			cursor: 'pointer',
		},

		center: {
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			width: '100%',
			height: '100%',
			flexDirection: 'column',
		},
	}),
	{
		name: 'FileReviewer',
	}
);

const FileReviewer = ({ onCloseclick, url }: any) => {
	const classes = useStyles();
	const refContainer: any = useRef();
	const refImage: any = useRef();
	const isMobile = useSelector((store: any) => store.socketio.isMobile);
	const dispatch = useDispatch();

	const clickOutSide = (e: any) => {
		if (refImage.current && !refImage.current.contains(e.target)) {
			onCloseclick();
		}
	};

	const handleKeyDown = (e: any) => {
		switch (e.key) {
			case 'Escape':
				onCloseclick();
				break;
			default:
				break;
		}
	};

	// 監聽器這條的依賴含每次 render 都新生的 handleKeyDown，所以每次 render 都會重掛。
	// 這件事本身無害（純掛／解監聽器，不改任何狀態），維持原樣。
	useEffect(() => {
		if (!url || isMobile) {
			return undefined;
		}
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [handleKeyDown, url, isMobile]);

	/**
	 * 檢視開關的通報必須自己一條，依賴只能是穩定值。
	 *
	 * 這裡會改 redux 狀態，一旦跟上面那條共用依賴就變成無限迴圈：每次 render 產生新的
	 * handleKeyDown → effect 重跑 → cleanup dispatch(false) 再 dispatch(true) → App
	 * 重新 render → 又一個新的 handleKeyDown …… 實測每秒約 50 圈、外框指令 101 次，
	 * 永遠不收斂，開一陣子分頁就當掉。
	 *
	 * url / isMobile 是值，dispatch 由 useDispatch 保證穩定，所以這條只在真正開關檢視
	 * 時各跑一次。
	 */
	useEffect(() => {
		if (!url || isMobile) {
			return undefined;
		}
		dispatch(setIsImageViewerOpen(true));
		// 過渡期給舊版 launch.js 的指令；新版靠 App 依 isImageViewerOpen 回報外框
		window.parent.postMessage({ target: 'eightChat', command: 'openImage' }, '*');

		return () => {
			dispatch(setIsImageViewerOpen(false));
			window.parent.postMessage({ target: 'eightChat', command: 'closeImage' }, '*');
		};
	}, [url, isMobile, dispatch]);

	const renderFile = () => {
		return <img ref={refImage} src={url} className={classes.img} alt="images" />;
	};
	return (
		<Portal>
			{url && (
				<div className={classes.root} onClick={clickOutSide}>
					<IconX id="x" onClick={onCloseclick} className={classes.closeStyle} color={color.$7} hoverColor={color.$7} />

					<div className={classes.center} ref={refContainer}>
						{renderFile()}
					</div>
				</div>
			)}
		</Portal>
	);
};

export default FileReviewer;
